import type { ComponentProps } from 'react';
import type React from 'react';

import { Spinner } from '@change/ds-core-ui';

import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { LazyComponent } from '#src/app/shared/components/layout/lazy';
import { useMediaQuery } from '#src/app/shared/hooks/dom';

import type { ShareContainer } from './Share';

async function loadShare(): Promise<typeof ShareContainer> {
	return (await import(/* webpackChunkName: "pages/petitionDetails-share" */ './Share')).ShareContainer;
}

type Props = ComponentProps<typeof ShareContainer> & { forMobileView: boolean };

const ShareLoaderInner: React.FC<Props> = ({ forMobileView: _forMobileView, ...restProps }) => {
	return (
		<LazyComponent loadComponent={loadShare} fallback={<Spinner className="mx-auto mt-16 size-6" />} {...restProps} />
	);
};

const ShareLoaderViewportGated: React.FC<Props> = ({ forMobileView, ...restProps }) => {
	const desktop = useMediaQuery('(min-width: 768px)');
	const viewportMatches = forMobileView ? !desktop : desktop;

	return viewportMatches ? <ShareLoaderInner forMobileView={forMobileView} {...restProps} /> : null;
};

export const ShareLoader: React.FC<Props> = ({ forMobileView, ...restProps }) => {
	if (forMobileView) {
		return <ShareLoaderViewportGated forMobileView={forMobileView} {...restProps} />;
	}

	return (
		<ClientRender placeholder={<Spinner className="mx-auto mt-16 size-6" />}>
			<ShareLoaderViewportGated forMobileView={forMobileView} {...restProps} />
		</ClientRender>
	);
};

// Re-exported by the sibling ./index.ts, which is in knip's `tempIgnored` list (vaquita squad cleanup,
// see knip.config.ts) — knip can't see that re-export as a real usage, so this needs @knip-ignore here.
/** @knip-ignore */
export const preloadShare = loadShare;
